import { useState } from 'react';
import { ChevronDown, ChevronRight, Calculator, FileText } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { SkeletonTable } from '../../components/ui/Skeleton';
import {
  useContabilidadReporte,
  useContabilidadPdfGeneral,
  useContabilidadPdfMaestro,
  type ContabilidadMaestro,
  type ContabilidadProyecto,
  type ContabilidadRubro,
  type ContabilidadMdoItem,
  type ContabilidadDescuentoItem,
  type ContabilidadSvItem,
} from '../../hooks/useContabilidad';

// ── Helpers ─────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Sub-components ───────────────────────────────────────────────────

function MdoSubRow({ item }: { item: ContabilidadMdoItem }) {
  return (
    <tr className="text-xs bg-gray-50/60">
      <td className="py-1.5 pr-4 pl-7 text-gray-500 italic">
        <span className="text-gray-300 mr-1.5">└</span>
        {item.mdo_nombre}
      </td>
      <td className="py-1.5 px-2 text-center text-gray-400 whitespace-nowrap">{item.mdo_unidad}</td>
      <td className="py-1.5 px-2 text-right text-gray-600">{fmt(item.cantidad)}</td>
      <td className="py-1.5 px-2 text-right text-gray-600">${fmt(item.precio_unitario)}</td>
      <td className="py-1.5 px-2 text-right text-gray-600">${fmt(item.subtotal)}</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 pl-2 text-right text-gray-300">—</td>
    </tr>
  );
}

function DescuentoSubRow({ item }: { item: ContabilidadDescuentoItem }) {
  return (
    <tr className="text-xs bg-red-50/50">
      <td className="py-1.5 pr-4 pl-7 text-gray-500 italic">
        <span className="text-red-200 mr-1.5">└</span>
        {item.descripcion}
      </td>
      <td className="py-1.5 px-2 text-center text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-red-500 font-semibold">-${fmt(item.monto)}</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 pl-2 text-right text-red-500 font-semibold">-${fmt(item.monto)}</td>
    </tr>
  );
}

function SvSubRow({ item }: { item: ContabilidadSvItem }) {
  return (
    <tr className="text-xs bg-gray-50/60">
      <td className="py-1.5 pr-4 pl-7 text-gray-500 italic">
        <span className="text-gray-300 mr-1.5">└</span>
        {item.descripcion}
      </td>
      <td className="py-1.5 px-2 text-center text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-600">${fmt(item.monto)}</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 px-2 text-right text-gray-300">—</td>
      <td className="py-1.5 pl-2 text-right text-gray-300">—</td>
    </tr>
  );
}

function RubroRow({ rubro }: { rubro: ContabilidadRubro }) {
  const hasMdo = rubro.mdo_items && rubro.mdo_items.length > 0;
  const hasDesc = rubro.desc_items && rubro.desc_items.length > 0;
  const hasSv = rubro.sv_items && rubro.sv_items.length > 0;
  const hideQtyPrice = hasMdo || hasDesc || hasSv;
  return (
    <>
      <tr className="border-b border-gray-100 text-sm">
        <td className="py-2 pr-4 text-gray-700 font-medium">{rubro.descripcion}</td>
        <td className="py-2 px-2 text-center text-gray-600 whitespace-nowrap">
          {rubro.unidad || <span className="text-gray-300">—</span>}
        </td>
        <td className="py-2 px-2 text-right text-gray-700">
          {hideQtyPrice ? <span className="text-gray-300">—</span> : fmt(rubro.cantidad)}
        </td>
        <td className="py-2 px-2 text-right text-gray-700">
          {hideQtyPrice ? <span className="text-gray-300">—</span> : `$${fmt(rubro.precio_unitario)}`}
        </td>
        <td className={`py-2 px-2 text-right ${hasDesc ? 'text-red-500 font-semibold' : 'text-gray-700'}`}>
          ${fmt(rubro.precio_total)}
        </td>
        <td className="py-2 px-2 text-right text-gray-500">{rubro.retencion_pct}%</td>
        <td className="py-2 px-2 text-right text-gray-500">${fmt(rubro.retenido)}</td>
        <td className={`py-2 pl-2 text-right font-semibold ${hasDesc ? 'text-red-500' : 'text-gray-800'}`}>
          ${fmt(rubro.a_pagar)}
        </td>
      </tr>
      {hasMdo && rubro.mdo_items!.map((item, i) => (
        <MdoSubRow key={i} item={item} />
      ))}
      {hasDesc && rubro.desc_items!.map((item, i) => (
        <DescuentoSubRow key={i} item={item} />
      ))}
      {hasSv && rubro.sv_items!.map((item, i) => (
        <SvSubRow key={i} item={item} />
      ))}
    </>
  );
}

function ProyectoSection({ proyecto }: { proyecto: ContabilidadProyecto }) {
  return (
    <div className="mb-4">
      <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {proyecto.project_nombre}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 text-left font-medium">Trabajo Realizado</th>
                <th className="py-2 px-2 text-center font-medium">Unid.</th>
                <th className="py-2 px-2 text-right font-medium">Cantidad</th>
                <th className="py-2 px-2 text-right font-medium">P. Unitario</th>
                <th className="py-2 px-2 text-right font-medium">P. Total</th>
                <th className="py-2 px-2 text-right font-medium">Retención</th>
                <th className="py-2 px-2 text-right font-medium">Retenido</th>
                <th className="py-2 pl-2 text-right font-medium">A Pagar</th>
              </tr>
            </thead>
            <tbody>
              {proyecto.rubros.map((r, i) => (
                <RubroRow key={i} rubro={r} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex justify-end">
          <span className="text-sm font-semibold text-gray-800">
            Total proyecto: ${fmt(proyecto.total_a_pagar)}
          </span>
        </div>
      </div>
    </div>
  );
}

function MaestroCard({
  maestro,
  desde,
  hasta,
}: {
  maestro: ContabilidadMaestro;
  desde: string;
  hasta: string;
}) {
  const [expanded, setExpanded] = useState(true);
  const pdfMaestro = useContabilidadPdfMaestro();

  return (
    <Card className="mb-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
            <div>
              <p className="font-semibold text-gray-900">{maestro.maestro_nombre}</p>
              <p className="text-xs text-gray-500">C.I. {maestro.maestro_cedula}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Gran Total</p>
            <p className="text-base font-bold text-blue-700">${fmt(maestro.gran_total)}</p>
          </div>
        </button>
        <div className="px-3 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              pdfMaestro.mutate({ desde, hasta, maestroId: maestro.maestro_id });
            }}
            isLoading={pdfMaestro.isPending}
            title="Exportar PDF de este maestro"
          >
            <FileText className="w-3.5 h-3.5 mr-1" />
            PDF
          </Button>
        </div>
      </div>

      {/* Projects */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          {maestro.proyectos.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">Sin proyectos en este período.</p>
          ) : (
            maestro.proyectos.map((p) => (
              <ProyectoSection key={p.project_id} proyecto={p} />
            ))
          )}
        </div>
      )}
    </Card>
  );
}

// ── Main page ────────────────────────────────────────────────────────

export function ContabilidadPage() {
  // Default period: current month
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);

  const [desde, setDesde] = useState(firstDay);
  const [hasta, setHasta] = useState(lastDay);
  const [queryDesde, setQueryDesde] = useState(firstDay);
  const [queryHasta, setQueryHasta] = useState(lastDay);

  const { data, isLoading, isError } = useContabilidadReporte(queryDesde, queryHasta);
  const pdfGeneral = useContabilidadPdfGeneral();

  function handleBuscar() {
    setQueryDesde(desde);
    setQueryHasta(hasta);
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
          <Calculator className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Reporte de Contabilidad</h1>
          <p className="text-sm text-gray-500">Pagos a maestros por quincena planillada</p>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[140px]">
            <Input
              label="Desde"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Input
              label="Hasta"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
          <Button onClick={handleBuscar} disabled={!desde || !hasta}>
            Consultar
          </Button>
          {data && !isLoading && (
            <Button
              variant="secondary"
              onClick={() => pdfGeneral.mutate({ desde: queryDesde, hasta: queryHasta })}
              isLoading={pdfGeneral.isPending}
            >
              <FileText className="w-4 h-4 mr-1" />
              PDF General
            </Button>
          )}
        </div>
      </Card>

      {/* Results */}
      {isLoading && (
        <div className="space-y-3">
          <SkeletonTable rows={4} />
        </div>
      )}

      {isError && (
        <Card className="p-6 text-center">
          <p className="text-red-500 text-sm">Error al cargar el reporte. Intente de nuevo.</p>
        </Card>
      )}

      {data && !isLoading && (
        <>
          {/* Summary bar */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              {data.maestros.length} maestro{data.maestros.length !== 1 ? 's' : ''} ·{' '}
              {queryDesde} al {queryHasta}
            </p>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total General</p>
              <p className="text-lg font-bold text-blue-700">${fmt(data.total_general)}</p>
            </div>
          </div>

          {data.maestros.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-400 text-sm">
                No hay planillas aprobadas o pagadas en este período.
              </p>
            </Card>
          ) : (
            data.maestros.map((m) => (
              <MaestroCard key={m.maestro_id} maestro={m} desde={queryDesde} hasta={queryHasta} />
            ))
          )}
        </>
      )}
    </div>
  );
}
